import { z } from "zod";

/**
 * Schema for single inventory item / product master
 */
export const itemSchema = z.object({
  name: z.string().min(1, "Item name is required"),
  itemCode: z.string().min(1, "Item code is required"),
  sku: z.string().nullable().optional(),
  category: z.string().min(1, "Category is required"),
  unit: z.string().min(1, "Unit is required"),
  unitPrice: z.number().min(0.01, "Purchase Rate is required and must be greater than 0"),
  minQty: z.number().min(0, "Minimum quantity cannot be negative").default(0),
  availableQty: z.number().min(1, "Quantity is required and must be at least 1"),
  brand: z.string().nullable().optional(),
  serviceType: z.enum(["verve"]).default("verve"),
  type: z.string().default("Stockable"),
  hsnCode: z.string().nullable().optional(),
  isUnique: z.boolean().default(false),
  uniqueName: z.string().nullable().optional(),
  hasBatchExpiry: z.boolean().default(false),
  expiryDate: z.string().nullable().optional(),
  barcode: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  bvPoints: z.number().min(0).default(0),
  taxP: z.number().min(0).default(0),
  
  // Financial breakdown fields for entries
  invoiceNo: z.string().optional(),
  invoiceDate: z.string().optional(),
  supplier: z.string().optional(),
  businessUnit: z.string().optional(),
  store: z.string().min(1, "Store is required"),
  baseAmount: z.number().min(0).default(0),
  discount: z.number().min(0).default(0),
  igst: z.number().min(0).default(0),
  sgst: z.number().min(0).default(0),
  cgst: z.number().min(0).default(0),
  taxAmount: z.number().min(0).default(0),
  chargeAmount: z.number().min(0).default(0),
  adjustments: z.number().default(0),
  netBill: z.number().min(0).default(0),
  netPayment: z.number().min(0).default(0),
  items: z.array(z.any()).optional(),
});


/**
 * Schema for Purchase Entry
 */
export const purchaseSchema = z.object({
  store: z.string().min(1, "Store location is required"),
  vendor: z.string().min(1, "Vendor is required"),
  billNo: z.string().min(1, "Bill number is required"),
  date: z.string().min(1, "Date is required"),
  items: z.array(z.object({
    item: z.string().min(1, "Item is required"),
    qty: z.number().positive("Quantity must be greater than 0"),
    freeQty: z.number().min(0).default(0),
    rate: z.number().min(0, "Rate cannot be negative"),
    discountPercent: z.number().min(0).max(100).default(0),
    taxPercent: z.number().min(0).default(0),
    bvPoints: z.number().min(0).default(0),
    hsnCode: z.string().optional(),
    serialNo: z.string().optional(),
    barcode: z.string().optional(),
    expiryDate: z.string().optional(),
  })).min(1, "At least one item is required"),
  charges: z.array(z.object({
    name: z.string().min(1, "Charge name is required"),
    amount: z.number().min(0),
  })).optional(),
  paymentMode: z.string().default("bank"),
  paidAmount: z.number().min(0).default(0),
  adjustment: z.number().default(0),
  remarks: z.string().optional(),
});


/**
 * Legacy validator wrapper for backward compatibility if needed
 * but we should prefer using the schemas directly with react-hook-form
 */
export const validateInventoryForm = (data) => {
  try {
    itemSchema.parse(data);
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = {};
      error.errors.forEach((err) => {
        if (err.path) errors[err.path[0]] = err.message;
      });
      return { isValid: false, errors };
    }
    return { isValid: false, errors: { _form: "Unknown error" } };
  }
};
